import React, { useEffect, useMemo, useState } from 'react'
import { FiSettings } from 'react-icons/fi'
import { AlertMsg, MwButton, MwInputFile, MwInputText, MwSpinnerButton } from '../components/ui'
import { useCurrent, usePost, usePut } from '../hooks'
import { useDispatch } from 'react-redux'
import { setisLogging } from '../store/authSlice'
import { useLocation } from 'react-router-dom'

const Settings = () => {
  const { user, currentLangId } = useCurrent()
  const dispatch = useDispatch();
  const location = useLocation();
  const { loading: savingRequest, putData } = usePut()
  const { data: uploadResult, error: uploadError, postData: uploadFile } = usePost(false);
  const [uploadingPic, setUploadingPic] = useState(false);
  
  const initialTab = useMemo(() => {
    const requested = location?.state?.tab;
    if (requested === 'password' || requested === 'profile') return requested;
    return 'profile';
  }, [location?.state?.tab]);
  
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const isAr = currentLangId === 'ar';

  // --- Profile State ---
  const [profileName, setProfileName] = useState(user?.name || '');
  const [profileMobile, setProfileMobile] = useState(user?.mobile || '');
  const [profileEmail, setProfileEmail] = useState(user?.email || '');
  const [profileJobTitle, setProfileJobTitle] = useState(user?.jobTitle || '');
  const [profilePic, setProfilePic] = useState(user?.userPic || '');
  const [profileAlert, setProfileAlert] = useState(null);

  useEffect(() => {
    setProfileName(user?.name || '');
    setProfileMobile(user?.mobile || '');
    setProfileEmail(user?.email || '');
    setProfileJobTitle(user?.jobTitle || '');
    setProfilePic(user?.userPic || '');
  }, [user?.email, user?.jobTitle, user?.mobile, user?.name, user?.userPic]);

  useEffect(() => {
    if (uploadResult?.data?.location) {
      setProfilePic(uploadResult.data.location);
      setUploadingPic(false);
    }
  }, [uploadResult]);

  useEffect(() => {
    if (uploadError) {
      setUploadingPic(false);
      setProfileAlert({ type: 'Error', msg: isAr ? 'فشل رفع الصورة' : 'Failed to upload image' });
    }
  }, [isAr, uploadError]);

  const uploadProfilePic = (e) => {
    const file = e?.target?.files?.[0] || null;
    if (!file) return;
    setProfileAlert(null);
    setUploadingPic(true);
    const formData = new FormData();
    formData.append('myfile', file, file.name);
    uploadFile(`${process.env.REACT_APP_UPLOADS_API_BASE_URL}/users`, formData, {
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
      timeout: 0,
    });
  };

  const submitProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileAlert(null);

    const name = String(profileName || '').trim();
    const mobile = String(profileMobile || '').trim();
    const email = String(profileEmail || '').trim();

    if (!name) {
      setProfileAlert({ type: 'Error', msg: isAr ? 'الاسم مطلوب' : 'Name is required' });
      return;
    }
    if (!email) {
      setProfileAlert({ type: 'Error', msg: isAr ? 'البريد الإلكتروني مطلوب' : 'Email is required' });
      return;
    }

    try {
      const result = await putData( 
        `${process.env.REACT_APP_Auth_API_BASE_URL}/users/update`,
        { name, mobile, email, jobTitle: profileJobTitle, userPic: profilePic }
      );
      const updatedUser = result?.data || result?.user || result?.data?.user || null;
      if (result?.status === 1 || result?.status === true || updatedUser) {
        const payload = updatedUser || { ...user, name, mobile, email, jobTitle: profileJobTitle, userPic: profilePic };
        localStorage.setItem('user', JSON.stringify(payload));
        localStorage.setItem('userCurrentTs', Date.now().toString());
        dispatch(setisLogging({ user: payload, isLogging: true }));
        setProfileAlert({ type: 'Success', msg: isAr ? 'تم تحديث بيانات الحساب بنجاح' : 'Account updated successfully' });
        return;
      }
      setProfileAlert({ type: 'Error', msg: result?.message || (isAr ? 'فشل تحديث الحساب' : 'Failed to update account') });
    } catch (err) {
      const apiMsg = err?.response?.data?.message;
      setProfileAlert({ type: 'Error', msg: apiMsg || (isAr ? 'فشل تحديث الحساب' : 'Failed to update account') });
    }
  };

  // --- Password State ---
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [passwordAlert, setPasswordAlert] = useState(null)

  const submitChangePassword = async (e) => {
    e.preventDefault()
    setPasswordAlert(null)

    const current = String(currentPassword || '').trim()
    const next = String(newPassword || '').trim()
    const confirm = String(confirmNewPassword || '').trim()

    if (!current || !next || !confirm) {
      setPasswordAlert({ type: 'Error', msg: isAr ? 'كل الحقول مطلوبة' : 'All fields are required' })
      return
    }
    if (next !== confirm) {
      setPasswordAlert({ type: 'Error', msg: isAr ? 'كلمة المرور غير متطابقة' : 'Passwords do not match' })
      return
    }
    if (next.length < 6) {
      setPasswordAlert({ type: 'Error', msg: isAr ? 'كلمة المرور قصيرة جداً' : 'Password is too short' })
      return
    }

    try {
      const result = await putData(
        `${process.env.REACT_APP_Auth_API_BASE_URL}/users/change-password`,
        { currentPassword: current, newPassword: next }
      )
      if (result?.status === 1) {
        setCurrentPassword('')
        setNewPassword('')
        setConfirmNewPassword('')
        setPasswordAlert({ type: 'Success', msg: isAr ? 'تم تغيير كلمة المرور بنجاح' : 'Password changed successfully' })
        return
      }
      setPasswordAlert({ type: 'Error', msg: result?.message || (isAr ? 'فشل تغيير كلمة المرور' : 'Failed to change password') })
    } catch (err) {
      const apiMsg = err?.response?.data?.message
      setPasswordAlert({ type: 'Error', msg: apiMsg || (isAr ? 'فشل تغيير كلمة المرور' : 'Failed to change password') })
    }
  }

  return (
    <div className="admin-page">
      <div className='admin-panel flex justify-between items-center p-5'>
        <div className='flex gap-2 items-center text-sm text-[#08203f] font-black'>
          <span className="w-9 h-9 rounded-xl bg-[#fff5d6] border border-[#f5b800]/25 flex items-center justify-center">
            <FiSettings size={20} className="text-[#08203f]" />
          </span>
          {isAr ? 'إعدادات النظام' : 'System Settings'}
        </div>
      </div>
      
      <div className='flex flex-col md:flex-row gap-6'>
        {/* Sidebar Tabs */}
        <div className='w-full md:w-56 shrink-0'>
          <ul className='bg-white rounded-2xl border border-[#f5b800]/20 shadow-sm overflow-hidden'>
            <li
              onClick={() => setActiveTab('profile')}
              className={`cursor-pointer flex items-center gap-3 px-4 py-3 border-b border-slate-100 transition-all ${
                activeTab === 'profile' ? 'bg-[#fff5d6] text-[#08203f] font-bold border-r-4 border-r-[#f5b800]' : 'text-slate-600 hover:bg-[#fffaf0]'
              }`}
            >
              {isAr ? 'حسابي' : 'My Account'}
            </li>
            <li
              onClick={() => setActiveTab('password')}
              className={`cursor-pointer flex items-center gap-3 px-4 py-3 transition-all ${
                activeTab === 'password' ? 'bg-[#fff5d6] text-[#08203f] font-bold border-r-4 border-r-[#f5b800]' : 'text-slate-600 hover:bg-[#fffaf0]'
              }`}
            >
              {isAr ? 'تغيير كلمة المرور' : 'Change Password'}
            </li>
          </ul>
        </div>

        {/* Content Area */}
        <div className='admin-panel flex-1 p-6'>
          {activeTab === 'profile' ? (
            <div className="max-w-xl">
              <h2 className="text-lg font-bold text-slate-800 mb-4">{isAr ? 'تحديث بيانات الحساب' : 'Update Account Details'}</h2>
              {profileAlert?.msg && (
                <AlertMsg type={profileAlert?.type === 'Error' ? 'Error' : undefined}>
                  {profileAlert.msg}
                </AlertMsg>
              )}
              <form onSubmit={submitProfileUpdate} className='flex flex-col gap-4'>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden border border-slate-200 bg-slate-50 flex items-center justify-center shrink-0">
                    {profilePic ? (
                      <img
                        src={profilePic}
                        alt={profileName || 'profile'}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <span className="text-slate-500 font-bold">{(profileName || '?').charAt(0)}</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <MwInputFile
                      id="profilePic"
                      label={isAr ? 'الصورة الشخصية' : 'Profile photo'}
                      acceptTypes="image/*"
                      loading={uploadingPic}
                      onChangeHandler={uploadProfilePic}
                      className="p-0"
                    />
                  </div>
                </div>
                <MwInputText
                  id="profileName"
                  label={isAr ? 'الاسم' : 'Name'}
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  classNameInput="text-start"
                />
                <MwInputText
                  id="profileJobTitle"
                  label={isAr ? 'المسمى الوظيفي' : 'Job title'}
                  value={profileJobTitle}
                  onChange={(e) => setProfileJobTitle(e.target.value)}
                  classNameInput="text-start"
                />
                <MwInputText
                  id="profileMobile"
                  label={isAr ? 'رقم الجوال' : 'Mobile'}
                  value={profileMobile}
                  onChange={(e) => setProfileMobile(e.target.value)}
                  classNameInput="text-start"
                />
                <MwInputText
                  id="profileEmail"
                  label={isAr ? 'البريد الإلكتروني' : 'Email'}
                  value={profileEmail}
                  onChange={(e) => setProfileEmail(e.target.value)}
                  classNameInput="text-start"
                />
                <div className='pt-2 flex items-center gap-2'>
                  <MwButton actionType="submit" type="saveBtn" disabled={savingRequest}>
                    {isAr ? 'حفظ التغييرات' : 'Save Changes'}
                  </MwButton>
                </div>
              </form>
            </div>
          ) : (
            <div className="max-w-xl">
              <h2 className="text-lg font-bold text-slate-800 mb-4">{isAr ? 'تغيير كلمة المرور' : 'Change Password'}</h2>
              {passwordAlert?.msg && (
                <AlertMsg type={passwordAlert?.type === 'Error' ? 'Error' : undefined}>
                  {passwordAlert.msg}
                </AlertMsg>
              )}
              <form onSubmit={submitChangePassword} className='flex flex-col gap-4'>
                <MwInputText
                  id="currentPassword"
                  label={isAr ? 'كلمة المرور الحالية' : 'Current password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  inputType="password"
                  classNameInput="text-start"
                />
                <MwInputText
                  id="newPassword"
                  label={isAr ? 'كلمة المرور الجديدة' : 'New password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  inputType="password"
                  classNameInput="text-start"
                />
                <MwInputText
                  id="confirmNewPassword"
                  label={isAr ? 'تأكيد كلمة المرور' : 'Confirm password'}
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  inputType="password"
                  classNameInput="text-start"
                />
                <div className='pt-2 flex items-center gap-2'>
                  <MwButton actionType="submit" type="saveBtn" disabled={savingRequest}>
                    {isAr ? 'تحديث كلمة المرور' : 'Update Password'}
                  </MwButton>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
export default Settings
